/* Completion dashboard for an in-house form — the Dashboard tab beside Results.
 *
 * The question this answers is "who still owes us this form", so the
 * denominator is the people HR granted it to (company_directory.enabled_forms),
 * not headcount. Everything here is counts, names and dates; no answer text
 * crosses this component, which is what lets it list the people who have NOT
 * filed. Unsubmitted drafts are deliberately invisible here — see
 * api/app/db/form_drafts.py for why nothing outside their author may see them.
 *
 * Written generically on `formKey`, so a second form's Tech page gets the same
 * tab by rendering <FormDashboard formKey="…" />.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale,
  Filler, Tooltip as ChartTooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useGetFormDashboardQuery } from '../../app/api';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Filler, ChartTooltip);

/* Palette.
 *
 * Progress is an ORDINAL scale — not started → submitted → assessed — so it is
 * one hue stepped light-to-dark, never three unrelated colors. The steps are
 * PCA Deep Teal Blue and two steps of its own ramp, validated as an ordinal
 * ramp against the white panel (monotone lightness, >=0.06 L between steps,
 * light end 2.11:1 against the surface, 12 degrees of hue spread). Do not
 * lighten the first step further — below 2:1 it stops reading as a mark.
 *
 * The page is light-mode only, like every other intranet page; there is no
 * theme toggle to respond to.
 */
const viz = {
  surface: '#ffffff',
  plane: '#fbfaf8',
  ink: '#0a2947',        // Deep Blue — headings
  body: '#58585b',       // Charcoal — body text
  muted: '#898781',      // axis/labels
  grid: '#e1e0d9',
  hairline: '#e9e6e1',
  link: '#0069a6',
  remaining: '#7fbada',  // ordinal step 1 — not submitted
  submitted: '#0069a6',  // ordinal step 2 — submitted
  assessed: '#00405f',   // ordinal step 3 — manager assessment filed
};

const pct = (part, whole) => (whole ? Math.round((1000 * part) / whole) / 10 : 0);
const DATE_OPTS = { month: 'short', day: 'numeric', year: 'numeric' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, DATE_OPTS) : '');
// A bare YYYY-MM-DD goes through Date() as UTC midnight, which renders as the
// day BEFORE anywhere west of Greenwich — and the whole firm is. Built as a
// local date instead, so the curve's axis matches the dates in the table.
const fmtDay = (ymd) => {
  const [y, m, d] = (ymd || '').split('-').map(Number);
  return y ? new Date(y, m - 1, d).toLocaleDateString(undefined, DATE_OPTS) : '';
};

/* One hover tooltip shared by every mark on the page. Marks are small and
 * scattered across three sections; a per-mark popover each with its own state
 * would be the same thing written five times. */
function useTooltip() {
  const [tip, setTip] = useState(null);
  const bind = (content) => ({
    onMouseEnter: (e) => setTip({ content, x: e.clientX, y: e.clientY }),
    onMouseMove: (e) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t)),
    onMouseLeave: () => setTip(null),
  });
  const node = tip ? (
    <div
      style={{
        position: 'fixed', left: tip.x + 12, top: tip.y + 12, zIndex: 2000,
        background: viz.ink, color: 'white', borderRadius: 6, padding: '0.4rem 0.6rem',
        fontSize: '0.75rem', fontWeight: 600, pointerEvents: 'none', maxWidth: 260,
        boxShadow: '0 4px 14px rgba(10,41,71,0.25)', lineHeight: 1.45,
      }}
    >
      {tip.content}
    </div>
  ) : null;
  return { bind, node };
}

/* A part-to-whole meter: filled steps then the lighter "remaining" step of the
 * same ramp, separated by 2px of surface rather than by strokes. */
function Meter({ segments, height, bind }) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const visible = segments.filter((s) => s.value > 0);
  // Spans, not divs: a breakdown row is a <button>, whose content model is
  // phrasing content.
  return (
    <span style={{ display: 'flex', gap: 2, height, width: '100%', background: viz.surface }}>
      {visible.map((s, i) => (
        <span
          key={s.label}
          {...bind(
            <>
              <div style={{ fontWeight: 800 }}>{s.label}</div>
              <div>{s.value} of {total} · {pct(s.value, total)}%</div>
            </>,
          )}
          style={{
            flex: `${s.value} 0 0`,
            background: s.color,
            borderTopLeftRadius: i === 0 ? 4 : 0,
            borderBottomLeftRadius: i === 0 ? 4 : 0,
            borderTopRightRadius: i === visible.length - 1 ? 4 : 0,
            borderBottomRightRadius: i === visible.length - 1 ? 4 : 0,
          }}
        />
      ))}
    </span>
  );
}
Meter.propTypes = {
  segments: PropTypes.array.isRequired,
  height: PropTypes.number.isRequired,
  bind: PropTypes.func.isRequired,
};

function Legend({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', marginTop: '0.55rem' }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: viz.body }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: it.color, flexShrink: 0 }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}
Legend.propTypes = { items: PropTypes.array.isRequired };

function StatTile({ label, value, sub }) {
  return (
    <div style={{ flex: '1 1 150px', background: viz.plane, border: `1px solid ${viz.hairline}`, borderRadius: 10, padding: '0.7rem 0.9rem' }}>
      <div style={{ fontSize: '0.72rem', color: viz.body, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: viz.ink, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: viz.muted }}>{sub}</div>}
    </div>
  );
}
StatTile.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, sub: PropTypes.node };

/* Cumulative completions. One series, so no legend box — the heading names it. */
function CompletionCurve({ timeline, eligible }) {
  const data = useMemo(() => ({
    labels: timeline.map((t) => fmtDay(t.date)),
    datasets: [{
      data: timeline.map((t) => t.cumulative),
      borderColor: viz.submitted,
      borderWidth: 2,
      backgroundColor: 'rgba(0,105,166,0.10)',   // series hue at 10% — a wash
      fill: true,
      tension: 0,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: viz.submitted,
      pointBorderColor: viz.surface,
      pointBorderWidth: 2,                       // 2px surface ring
    }],
  }), [timeline]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: viz.ink,
        padding: 10,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          label: (ctx) => {
            const row = timeline[ctx.dataIndex];
            return `${row.cumulative} of ${eligible} completed (+${row.count} that day)`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: viz.muted, font: { size: 11 }, maxTicksLimit: 8 } },
      y: {
        beginAtZero: true,
        suggestedMax: eligible,
        grid: { color: viz.grid },
        border: { display: false },
        ticks: { color: viz.muted, font: { size: 11 }, precision: 0, maxTicksLimit: 5 },
      },
    },
  }), [timeline, eligible]);

  return <div style={{ height: 180 }}><Line data={data} options={options} /></div>;
}
CompletionCurve.propTypes = { timeline: PropTypes.array.isRequired, eligible: PropTypes.number.isRequired };

/* One breakdown row: group name, meter, and the percentage direct-labelled at
 * the end. Clicking filters the roster below — that is the drill-down. */
function BreakdownRow({ group, withAssessment, selected, onSelect, bind }) {
  const done = withAssessment ? (group.assessed || 0) : 0;
  const submittedOnly = group.completed - done;
  const segments = [
    ...(withAssessment ? [{ label: 'Manager assessment filed', value: done, color: viz.assessed }] : []),
    { label: withAssessment ? 'Submitted, no assessment yet' : 'Submitted', value: submittedOnly, color: viz.submitted },
    { label: 'Not submitted', value: group.eligible - group.completed, color: viz.remaining },
  ];
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? null : group.label)}
      style={{
        display: 'grid', gridTemplateColumns: 'minmax(110px, 190px) 1fr 92px', gap: '0.75rem',
        alignItems: 'center', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: selected ? viz.plane : 'transparent',
        border: `1px solid ${selected ? viz.hairline : 'transparent'}`,
        borderRadius: 8, padding: '0.4rem 0.5rem',
      }}
    >
      <span style={{
        fontSize: '0.8rem', fontWeight: 700, color: group.is_unset ? viz.muted : viz.ink,
        fontStyle: group.is_unset ? 'italic' : 'normal',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {group.label}
      </span>
      {/* 20px: at the mark cap of 24 and tall enough that a segment is an easy
          hover target for its tooltip. */}
      <Meter segments={segments} height={20} bind={bind} />
      <span style={{ fontSize: '0.78rem', color: viz.body, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <strong style={{ color: viz.ink }}>{group.pct}%</strong>
        <span style={{ color: viz.muted }}> · {group.completed}/{group.eligible}</span>
      </span>
    </button>
  );
}
BreakdownRow.propTypes = {
  group: PropTypes.object.isRequired,
  withAssessment: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  bind: PropTypes.func.isRequired,
};

/* The table view. Required as the non-color channel for everything above, and
 * it is also the list someone actually chases people from. */
function Roster({ people, withAssessment, statusFilter, onStatusFilter, groupKey, groupValue, onClearGroup }) {
  const rows = useMemo(() => people.filter((p) => {
    if (groupValue && (p.groups || {})[groupKey] !== groupValue) return false;
    if (statusFilter === 'completed') return p.submitted;
    if (statusFilter === 'outstanding') return !p.submitted;
    if (statusFilter === 'awaiting_manager') return p.submitted && !p.assessed;
    return true;
  }), [people, statusFilter, groupKey, groupValue]);

  const filters = [
    { key: 'all', label: 'Everyone' },
    { key: 'outstanding', label: 'Not submitted' },
    { key: 'completed', label: 'Submitted' },
    ...(withAssessment ? [{ key: 'awaiting_manager', label: 'No assessment yet' }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginBottom: '0.6rem' }}>
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => onStatusFilter(f.key)}
            style={{
              background: statusFilter === f.key ? viz.ink : viz.surface,
              color: statusFilter === f.key ? 'white' : viz.body,
              border: `1px solid ${statusFilter === f.key ? viz.ink : viz.hairline}`,
              borderRadius: 999, padding: '0.22rem 0.7rem', fontSize: '0.75rem',
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
        {groupValue && (
          <button
            type="button"
            onClick={onClearGroup}
            style={{
              background: viz.plane, color: viz.ink, border: `1px solid ${viz.hairline}`,
              borderRadius: 999, padding: '0.22rem 0.7rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            {groupValue} <i className="bi bi-x-lg ms-1" style={{ fontSize: '0.62rem' }} />
          </button>
        )}
        <span style={{ color: viz.muted, fontSize: '0.75rem', marginLeft: 'auto' }}>
          {rows.length} {rows.length === 1 ? 'person' : 'people'}
        </span>
      </div>

      <div style={{ border: `1px solid ${viz.hairline}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: viz.plane, color: viz.body, textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>Name</th>
              <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>Title</th>
              <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>Manager</th>
              <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>Status</th>
              <th style={{ padding: '0.5rem 0.75rem', fontWeight: 700, textAlign: 'right' }}>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: '1.25rem', textAlign: 'center', color: viz.muted }}>Nobody matches that filter.</td></tr>
            )}
            {rows.map((p) => {
              // Same ordinal step as the bars carry, but the word beside the dot
              // is what conveys the state — color never does it alone.
              const state = !p.submitted ? { color: viz.remaining, text: 'Not submitted' }
                : (withAssessment && !p.assessed)
                  ? { color: viz.submitted,
                      text: p.assessment_requested ? 'Awaiting manager' : 'Manager not asked yet' }
                  : { color: withAssessment ? viz.assessed : viz.submitted, text: 'Complete' };
              return (
              <tr key={p.directory_id} style={{ borderTop: `1px solid ${viz.hairline}` }}>
                <td style={{ padding: '0.5rem 0.75rem', color: viz.ink, fontWeight: 700 }}>
                  {p.name}
                  <div style={{ fontWeight: 400, color: viz.muted, fontSize: '0.72rem' }}>{p.email}</div>
                </td>
                <td style={{ padding: '0.5rem 0.75rem', color: viz.body }}>{p.title || '—'}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: viz.body }}>{p.manager_name || '—'}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: viz.body }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: state.color }} />
                    {state.text}
                  </span>
                </td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: viz.body, fontVariantNumeric: 'tabular-nums' }}>
                  {p.submitted_at ? fmtDate(p.submitted_at) : '—'}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
Roster.propTypes = {
  people: PropTypes.array.isRequired,
  withAssessment: PropTypes.bool,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilter: PropTypes.func.isRequired,
  groupKey: PropTypes.string,
  groupValue: PropTypes.string,
  onClearGroup: PropTypes.func.isRequired,
};

function Section({ title, hint, children }) {
  return (
    <section style={{ marginTop: '1.5rem' }}>
      <h4 style={{ color: viz.ink, fontWeight: 800, fontSize: '0.95rem', margin: 0 }}>{title}</h4>
      {hint && <div style={{ color: viz.body, fontSize: '0.78rem', margin: '0.15rem 0 0.7rem' }}>{hint}</div>}
      {children}
    </section>
  );
}
Section.propTypes = { title: PropTypes.string.isRequired, hint: PropTypes.node, children: PropTypes.node };

export default function FormDashboard({ formKey }) {
  const { data, isLoading, isError } = useGetFormDashboardQuery({ form_key: formKey });
  const [dimension, setDimension] = useState(null);
  const [groupValue, setGroupValue] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const { bind, node: tooltip } = useTooltip();

  const breakdowns = data?.breakdowns || [];
  const activeDimension = breakdowns.find((b) => b.key === dimension) || breakdowns[0] || null;
  const withAssessment = Boolean(data?.assessment);

  if (isLoading) return <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>;
  if (isError) return <div style={{ textAlign: 'center', color: viz.body, padding: '2rem' }}>Unable to load the dashboard.</div>;
  if (!data) return null;

  if (data.eligible === 0) {
    return (
      <div style={{ textAlign: 'center', color: viz.body, padding: '2.5rem 1rem' }}>
        <i className="bi bi-people" style={{ fontSize: '1.6rem', color: viz.muted }} />
        <div style={{ fontWeight: 700, color: viz.ink, marginTop: '0.5rem' }}>Nobody can see this form yet.</div>
        <div style={{ fontSize: '0.83rem', marginTop: '0.25rem' }}>
          HR grants it per person on Manage Directory. Completion is measured against that list,
          so there is nothing to report until someone is on it.
        </div>
      </div>
    );
  }

  const assessedCount = withAssessment ? data.assessment.completed : 0;
  const overall = [
    ...(withAssessment ? [{ label: 'Manager assessment filed', value: assessedCount, color: viz.assessed }] : []),
    { label: withAssessment ? 'Submitted, no assessment yet' : 'Submitted', value: data.completed - assessedCount, color: viz.submitted },
    { label: 'Not submitted', value: data.outstanding, color: viz.remaining },
  ];

  return (
    <div>
      {tooltip}

      {/* Hero figure — the one number the tab leads with. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '3rem', fontWeight: 800, color: viz.ink, lineHeight: 1 }}>{data.pct}%</span>
        <span style={{ color: viz.body, fontSize: '0.9rem' }}>
          completed — {data.completed} of the {data.eligible} {data.eligible === 1 ? 'person' : 'people'} this form is visible to
        </span>
      </div>
      <div style={{ marginTop: '0.85rem' }}>
        <Meter segments={overall} height={24} bind={bind} />
        <Legend items={overall.map((s) => ({ label: s.label, color: s.color }))} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '1.1rem' }}>
        <StatTile label="Can see the form" value={data.eligible}
          sub={`of ${data.active_headcount} active employees`} />
        <StatTile label="Submitted" value={data.completed} sub={`${data.pct}% of those granted`} />
        <StatTile label="Still outstanding" value={data.outstanding}
          sub={data.outstanding === 0 ? 'Everyone is done' : 'Listed below'} />
        {withAssessment && (
          <StatTile
            label={data.assessment.label}
            value={`${data.assessment.completed} of ${data.assessment.eligible}`}
            sub={data.assessment.eligible === 0
              ? 'No assessments requested yet'
              : (
                <>
                  {data.assessment.pct}% of those requested
                  {data.assessment.not_requested > 0
                    && ` · ${data.assessment.not_requested} not requested yet`}
                </>
              )}
          />
        )}
      </div>

      {/* Granted to a sliver of the firm? Say so. Otherwise "100% complete"
          reads the same whether the form went to everyone or to three people. */}
      {data.eligible < data.active_headcount && (
        <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: viz.body, background: viz.plane, border: `1px solid ${viz.hairline}`, borderRadius: 8, padding: '0.5rem 0.75rem' }}>
          <i className="bi bi-info-circle me-2" style={{ color: viz.link }} />
          This percentage is of the {data.eligible} {data.eligible === 1 ? 'person' : 'people'} HR has granted the form to,
          not of all {data.active_headcount} active employees. Grant it to more people on HR &rarr; Manage Directory.
        </div>
      )}
      {data.unmatched_submissions > 0 && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#8a5a00', background: '#FDF0E0', border: '1px solid #f0d9b5', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
          <i className="bi bi-exclamation-triangle me-2" />
          {data.unmatched_submissions} submission{data.unmatched_submissions === 1 ? '' : 's'} on the Results tab
          {data.unmatched_submissions === 1 ? ' is' : ' are'} not counted here — the submitter has since been
          marked terminated, had the form removed, or was never linked to a directory entry.
        </div>
      )}

      {data.timeline.length >= 2 && (
        <Section title="Completed over time" hint="Running total of submissions.">
          <CompletionCurve timeline={data.timeline} eligible={data.eligible} />
        </Section>
      )}

      {activeDimension && (
        <Section
          title="Completion by group"
          hint="Click a row to filter the list below to those people."
        >
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {breakdowns.map((b) => (
              <button
                key={b.key}
                type="button"
                onClick={() => { setDimension(b.key); setGroupValue(null); }}
                style={{
                  background: b.key === activeDimension.key ? viz.ink : viz.surface,
                  color: b.key === activeDimension.key ? 'white' : viz.body,
                  border: `1px solid ${b.key === activeDimension.key ? viz.ink : viz.hairline}`,
                  borderRadius: 6, padding: '0.28rem 0.75rem', fontSize: '0.78rem',
                  fontWeight: 700, cursor: 'pointer',
                }}
              >
                {b.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {activeDimension.groups.map((g) => (
              <BreakdownRow
                key={g.label}
                group={g}
                withAssessment={withAssessment}
                selected={groupValue === g.label}
                onSelect={setGroupValue}
                bind={bind}
              />
            ))}
          </div>
          <Legend items={overall.map((s) => ({ label: s.label, color: s.color }))} />
        </Section>
      )}

      <Section title="People">
        <Roster
          people={data.people}
          withAssessment={withAssessment}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          groupKey={activeDimension ? activeDimension.key : null}
          groupValue={groupValue}
          onClearGroup={() => setGroupValue(null)}
        />
      </Section>
    </div>
  );
}
FormDashboard.propTypes = { formKey: PropTypes.string.isRequired };
